import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Typography } from 'antd';
import { Box, Text } from 'atoms';
import ProgressBar from 'components/ProgressBar';

const { Title } = Typography;

const SummaryCard = styled(Box)`
	background-color: #fff;
	border-radius: 8px;
	border-top: 2px solid #2f8ecb;
	margin-bottom: 24px;
	padding: 24px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SummaryTitle = styled(Title)`
	font-size: 18px !important;
	font-weight: 700 !important;
	text-transform: uppercase;
	margin-bottom: 20px !important;
	color: #333;
	letter-spacing: 0.5px;
`;

const SummaryGrid = styled(Row)`
	margin-bottom: 20px;
`;

const SummaryItem = styled(Col)`
	margin-bottom: 12px;
`;

const Label = styled(Text)`
	font-size: 12px;
	font-weight: 600;
	color: #666;
	text-transform: uppercase;
	display: block;
	margin-bottom: 6px;
	letter-spacing: 0.3px;
`;

const Value = styled(Text)`
	font-size: 15px;
	font-weight: 500;
	color: #333;
	display: block;
`;

const PaymentStatusContainer = styled.div`
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e8e8e8;
`;

const PaymentStatusHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
`;

const PaymentStatusLabel = styled(Text)`
	font-size: 13px;
	font-weight: 600;
	color: #333;
	text-transform: uppercase;
	letter-spacing: 0.3px;
`;

const PaymentBreakdownInline = styled.div`
	display: flex;
	gap: 24px;
	align-items: center;
`;

const ProgressBarWrapper = styled.div`
	margin-bottom: 8px;
	background-color: #eefff1;
	border-radius: 4px;
	overflow: hidden;
`;

const StatusTag = styled.div`
	display: inline-block;
	padding: 4px 12px;
	border-radius: 4px;
	background-color: ${(props) => (props.isPaid ? '#e6ffed' : '#fff9e6')};
	margin-top: 8px;
	margin-bottom: 16px;
`;

const StatusTagText = styled(Text)`
	font-size: 11px;
	font-weight: 700;
	color: ${(props) => (props.isPaid ? '#4caf50' : '#ff9800')};
	text-transform: uppercase;
	letter-spacing: 0.5px;
`;

const PaymentItem = styled.div`
	flex: 0 0 auto;
	white-space: nowrap;
`;

const PaymentItemLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 500;
	margin-right: 4px;
`;

const PaymentItemValue = styled(Text)`
	font-size: 15px;
	font-weight: 700;
	color: ${(props) => props.color || '#333'};
`;

const TripSummary = styled.div`
	display: flex;
	gap: 32px;
	margin-top: 12px;
	align-items: flex-start;
`;

const TripItem = styled.div`
	flex: 0 0 auto;
	text-align: left;
	min-width: 80px;
`;

const TripItemLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 6px;
	font-weight: 500;
`;

const TripItemValue = styled(Text)`
	font-size: 16px;
	font-weight: 700;
	color: #333;
	display: block;
`;

// Helper to parse numeric value (handles string and number)
const parseNumericValue = (value, defaultValue = 0) => {
	if (value === null || value === undefined || value === '') return defaultValue;
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const parsed = parseFloat(value);
		return Number.isNaN(parsed) ? defaultValue : parsed;
	}
	return defaultValue;
};

function InvoiceSummaryCard({ vendorBillRes, vendorDetails }) {
	const vendorBill = vendorBillRes?.vendor_bill || {};

	// Use API values directly from vendor_bill
	const totalAmount = parseNumericValue(vendorBill?.total_payable_amount, 0);
	const paidAmount = parseNumericValue(vendorBill?.paid_amount, 0);
	const pendingAmount = parseNumericValue(vendorBill?.pending_amount, 0);
	const invoiceStatus = vendorBill?.invoice_status;
	const paidAmountPercentage = parseNumericValue(vendorBill?.paid_amount_percentage, 0);

	// Calculate trip counts
	const regularTrips = vendorBillRes?.regular_trips_bill || [];
	const adhocTrips = vendorBillRes?.adhoc_trips_bill || [];
	const noLoadTrips = vendorBillRes?.noload_trips_bill || [];

	const totalRegularTrips = regularTrips.reduce((sum, trip) => sum + (trip.no_of_trips || 0), 0);
	const totalAdhocTrips = adhocTrips.reduce((sum, trip) => sum + (trip.no_of_trips || 0), 0);
	const totalNoLoadTrips = noLoadTrips.reduce((sum, trip) => sum + (trip.no_of_trips || 0), 0);
	const totalTrips = totalRegularTrips + totalAdhocTrips + totalNoLoadTrips;

	// Get project name from project_location_codes array
	const projectLocationCodes = vendorBillRes?.project_location_codes || [];
	const projectName =
		projectLocationCodes.length > 0
			? projectLocationCodes.join(', ')
			: vendorBill?.project_name || vendorBillRes?.project_name || '-';

	// Get vehicle type from adhoc_trips_bill or regular_trips_bill
	const vehicleTypeFromAdhoc = adhocTrips.length > 0 ? adhocTrips[0]?.vehicle_type : null;
	const vehicleType =
		vehicleTypeFromAdhoc || vendorBill?.vehicle_type || vendorBillRes?.vehicle_type || '-';

	const billingPeriod = vendorBill?.bill_period || '-';

	// Format currency values
	const formatCurrency = (amount) =>
		amount.toLocaleString('en-IN', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});

	const isPaidStatus = pendingAmount <= 0;

	// For pending invoices, show remaining percentage = 100 - paid_amount_percentage
	// Once fully paid or not pending, show paid percentage directly
	const rawProgressPercentage =
		!isPaidStatus && invoiceStatus === 'Pending'
			? Math.max(0, Math.min(100, 100 - paidAmountPercentage))
			: paidAmountPercentage;

	const paymentPercentageDisplay = Number(rawProgressPercentage || 0).toFixed(0);

	return (
		<SummaryCard>
			<SummaryTitle level={4}>INVOICE SUMMARY</SummaryTitle>

			<SummaryGrid gutter={[24, 12]}>
				<SummaryItem xs={24} sm={12} md={6}>
					<Label>VENDOR</Label>
					<Value>{vendorDetails?.vendor_name || vendorBill?.vendor_name || '-'}</Value>
				</SummaryItem>
				<SummaryItem xs={24} sm={12} md={6}>
					<Label>PROJECT</Label>
					<Value>{projectName}</Value>
				</SummaryItem>
				<SummaryItem xs={24} sm={12} md={6}>
					<Label>VEHICLE TYPE</Label>
					<Value>{vehicleType}</Value>
				</SummaryItem>
				<SummaryItem xs={24} sm={12} md={6}>
					<Label>BILLING PERIOD</Label>
					<Value>{billingPeriod}</Value>
				</SummaryItem>
			</SummaryGrid>

			<PaymentStatusContainer>
				<PaymentStatusHeader>
					<PaymentStatusLabel>PAYMENT STATUS:</PaymentStatusLabel>
					<PaymentBreakdownInline>
						<PaymentItem>
							<PaymentItemLabel>Total Amount:</PaymentItemLabel>
							<PaymentItemValue>₹{formatCurrency(totalAmount)}</PaymentItemValue>
						</PaymentItem>
						<PaymentItem>
							<PaymentItemLabel>Pending Amount:</PaymentItemLabel>
							<PaymentItemValue color="#ff9800">
								₹{formatCurrency(pendingAmount)}
							</PaymentItemValue>
						</PaymentItem>
						<PaymentItem>
							<PaymentItemLabel>Paid:</PaymentItemLabel>
							<PaymentItemValue color="#4caf50">
								₹{formatCurrency(paidAmount)}
							</PaymentItemValue>
						</PaymentItem>
					</PaymentBreakdownInline>
				</PaymentStatusHeader>
				<ProgressBarWrapper>
					<ProgressBar percentage={paidAmountPercentage} height={8} spliting={4} />
				</ProgressBarWrapper>
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-start',
						gap: '24px',
						marginTop: '8px',
					}}
				>
					<StatusTag isPaid={isPaidStatus}>
						<StatusTagText isPaid={isPaidStatus}>
							{pendingAmount > 0 ? 'PENDING' : 'PAID'}
						</StatusTagText>
					</StatusTag>
					<Text
						style={{
							marginLeft: '-15px',
							marginTop: '11px',
							fontSize: 12,
							fontWeight: 600,
							color: isPaidStatus ? '#4caf50' : '#ff9800',
						}}
					>
						({paymentPercentageDisplay}%)
					</Text>
					<TripSummary>
						<TripItem>
							<TripItemLabel>Total Trips</TripItemLabel>
							<TripItemValue>{totalTrips}</TripItemValue>
						</TripItem>
						<TripItem>
							<TripItemLabel>Regular</TripItemLabel>
							<TripItemValue>{totalRegularTrips}</TripItemValue>
						</TripItem>
						<TripItem>
							<TripItemLabel>Ad-hoc</TripItemLabel>
							<TripItemValue>{totalAdhocTrips}</TripItemValue>
						</TripItem>
					</TripSummary>
				</div>
			</PaymentStatusContainer>
		</SummaryCard>
	);
}

InvoiceSummaryCard.propTypes = {
	vendorBillRes: PropTypes.object,
	vendorDetails: PropTypes.object,
};

export default InvoiceSummaryCard;
